// =============================================================================
// pixelIcons.ts — Ícones pixel art em SVG inline (issues #65 e #31: NADA de
// emoji). Cada ícone é uma grade 12x12 de <rect> com crispEdges, no espírito
// 8-bit do mockup. Aqui moram: os santinhos do mural de salas, o gabinete de
// ícones de ARMAS e UPGRADES (urna, HUD, placar e comitê), os selos da urna
// (recontagem/cassação/abstenção e a Emenda de Relator dourada) e os ícones
// de stats do cartão de candidato.
// =============================================================================
import { WEAPON_TABLE } from '../../../shared/weapons';

type Cell = [x: number, y: number, w?: number, h?: number];

// Um "carimbo" de pixels de uma cor só
const g = (fill: string, cells: Cell[]): string =>
  `<g fill="${fill}">${cells
    .map(([x, y, w = 1, h = 1]) => `<rect x="${x}" y="${y}" width="${w}" height="${h}"/>`)
    .join('')}</g>`;

const svg = (inner: string, size = 20): string =>
  `<svg class="pix-icon" width="${size}" height="${size}" viewBox="0 0 12 12" `
  + `shape-rendering="crispEdges" aria-hidden="true">${inner}</svg>`;

// -----------------------------------------------------------------------------
// O gabinete de ícones: um por "temática" de comitê
// -----------------------------------------------------------------------------
// Congresso: cúpula, colunas e escadaria — onde tudo se decide (ou se engaveta)
const CONGRESSO =
  g('#e8eaee', [[1, 4, 10, 1], [1, 10, 10, 1]])
  + g('#cfd2d6', [[4, 2, 4, 1], [3, 3, 6, 1], [2, 5, 8, 5]])
  + g('#565b63', [[3, 6, 1, 3], [5, 6, 1, 3], [7, 6, 1, 3]]);

// Balão de zap: a fonte oficial de informação da família
const ZAP =
  g('#2ecc71', [[2, 2, 8, 5], [1, 3, 1, 3], [10, 3, 1, 3], [3, 7, 2, 1], [3, 8, 1, 1]])
  + g('#0b3d22', [[4, 4, 1, 1], [6, 4, 1, 1], [8, 4, 1, 1]]);

// Bandeirinha do Brasil de comício (a de plástico, feita na China)
const BANDEIRA =
  g('#b3ab9c', [[2, 1, 1, 10]])
  + g('#1e9e4e', [[3, 1, 8, 6]])
  + g('#f7d117', [[6, 2, 2, 1], [5, 3, 4, 1], [6, 4, 2, 1]])
  + g('#16324a', [[6, 3, 2, 1]]);

// Chopp da mesa de bar: onde nasce todo cientista político
const CHOPP =
  g('#f4f1ea', [[3, 2, 6, 2]])
  + g('#f7d117', [[3, 4, 6, 6]])
  + g('#ffe14d', [[4, 5, 1, 4]])
  + g('#d8d3c8', [[9, 4, 2, 1], [10, 5, 1, 3], [9, 8, 2, 1]]);

// Estrela de partido (qualquer um, todas brilham igual na hora da propina)
const ESTRELA =
  g('#f7d117', [
    [5, 1, 2, 2], [4, 3, 4, 1], [1, 4, 10, 1], [2, 5, 8, 1], [3, 6, 6, 1],
    [3, 7, 2, 1], [7, 7, 2, 1], [2, 8, 2, 1], [8, 8, 2, 1],
  ]);

// Maleta de dinheiro vivo (uso estritamente decorativo, juro)
const MALETA =
  g('#5c4326', [[4, 1, 4, 1], [4, 2, 1, 1], [7, 2, 1, 1]])
  + g('#8a5a2b', [[1, 3, 10, 7]])
  + g('#6d4520', [[1, 6, 10, 1]])
  + g('#f7d117', [[5, 5, 2, 3]]);

const ICONES_SALA = [CONGRESSO, ZAP, BANDEIRA, CHOPP, ESTRELA, MALETA];

// Ícone determinístico a partir do código da sala: a mesma sala mantém o
// mesmo santinho enquanto o mural atualiza sozinho
export function roomPixelIcon(code: string): string {
  let hash = 0;
  for (const ch of code) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return svg(ICONES_SALA[hash % ICONES_SALA.length]);
}

// Setinhas do seletor de skins (issue #24): chevron pixelado, sem emoji
const SETA_ESQ = g('currentColor', [
  [7, 2, 2, 1], [6, 3, 2, 1], [5, 4, 2, 1], [4, 5, 2, 1],
  [4, 6, 2, 1], [5, 7, 2, 1], [6, 8, 2, 1], [7, 9, 2, 1],
]);
const SETA_DIR = g('currentColor', [
  [3, 2, 2, 1], [4, 3, 2, 1], [5, 4, 2, 1], [6, 5, 2, 1],
  [6, 6, 2, 1], [5, 7, 2, 1], [4, 8, 2, 1], [3, 9, 2, 1],
]);

export function arrowPixelIcon(dir: 'esq' | 'dir', size = 12): string {
  return svg(dir === 'esq' ? SETA_ESQ : SETA_DIR, size);
}

// -----------------------------------------------------------------------------
// Gabinete de ícones de ARMAS (issue #31): um santinho pixel art por arma,
// usado na urna, no arsenal do HUD, no placar da CPI e no comitê de campanha
// -----------------------------------------------------------------------------
// (Os santinhos aposentados pela issue #121 — microfone, pomba, garrafa,
// panela, holofote e casinha — foram cassados junto com os nomes antigos.)

// Carteira de trabalho (CTPS azul com brasão)
const CTPS =
  g('#12295e', [[2, 1, 1, 10]])
  + g('#1a3d8f', [[3, 1, 7, 10]])
  + g('#d4af37', [[5, 4, 3, 2], [6, 6, 1, 1], [6, 3, 1, 1]]);

// Cruz litúrgica dourada
const CRUZ =
  g('#d4af37', [[5, 1, 2, 10], [2, 4, 8, 2]])
  + g('#a8862a', [[6, 1, 1, 10], [2, 5, 8, 1]]);

// Morcego da madrugada
const MORCEGO =
  g('#3a2d4f', [[4, 2, 1, 1], [7, 2, 1, 1], [1, 4, 3, 1], [8, 4, 3, 1], [0, 5, 2, 1], [10, 5, 2, 1], [4, 3, 4, 4], [5, 7, 2, 1]])
  + g('#e74c3c', [[5, 4, 1, 1], [7, 4, 1, 1]]);

// Cadeira de debate (madeira de lei)
const CADEIRA =
  g('#8a5a2b', [[3, 1, 4, 1], [3, 2, 1, 4], [3, 6, 6, 1], [3, 7, 1, 4], [8, 7, 1, 4]])
  + g('#6d4520', [[4, 2, 3, 4]]);

// Megafone de fake news (com ondas)
const MEGAFONE =
  g('#c1272d', [[2, 4, 5, 4], [1, 5, 1, 2]])
  + g('#e74c3c', [[7, 3, 2, 6]])
  + g('#7a3226', [[3, 8, 2, 3]])
  + g('#f7d117', [[10, 2, 1, 1], [11, 4, 1, 2], [10, 7, 1, 1]]);

// Diamante do PIX
const PIX =
  g('#2ecc71', [[5, 1, 2, 1], [4, 2, 4, 1], [3, 3, 6, 1], [2, 4, 8, 2], [3, 6, 6, 1], [4, 7, 4, 1], [5, 8, 2, 1]])
  + g('#0b3d22', [[5, 4, 2, 2]]);

// Mala de dinheiro do mensalão (variação da maleta do mural, com cifrões)
const MENSALAO =
  g('#5c4326', [[4, 1, 4, 1], [4, 2, 1, 1], [7, 2, 1, 1]])
  + g('#8a5a2b', [[1, 3, 10, 7]])
  + g('#6d4520', [[1, 6, 10, 1]])
  + g('#2ecc71', [[3, 4, 2, 2], [7, 7, 2, 2]])
  + g('#f7d117', [[5, 5, 2, 3]]);

// Explosão do quebra-quebra
const EXPLOSAO =
  g('#e74c3c', [[5, 0, 2, 2], [0, 5, 2, 2], [10, 5, 2, 2], [5, 10, 2, 2], [2, 2, 2, 2], [8, 2, 2, 2], [2, 8, 2, 2], [8, 8, 2, 2]])
  + g('#f7a117', [[4, 3, 4, 1], [3, 4, 6, 4], [4, 8, 4, 1]])
  + g('#ffe14d', [[5, 5, 2, 2]]);

// Cristal de gelo monocrático
const GELO =
  g('#a8d8f0', [[5, 1, 2, 10], [1, 5, 10, 2]])
  + g('#d0ecf8', [[3, 3, 1, 1], [8, 3, 1, 1], [3, 8, 1, 1], [8, 8, 1, 1], [2, 2, 1, 1], [9, 2, 1, 1], [2, 9, 1, 1], [9, 9, 1, 1]])
  + g('#f4fbff', [[5, 5, 2, 2]]);

// ---- Ícones novos da issue #121 (armas retematizadas) -----------------------
// Soluço Patriótico: boca engasgada soltando ondas de "HIC!"
const SOLUCO =
  g('#f7d117', [[1, 5, 2, 2]])
  + g('#ffe14d', [[4, 4, 1, 4], [6, 3, 1, 6], [8, 2, 1, 8]])
  + g('#fff8c8', [[10, 1, 1, 10]]);

// Pão com mortadela (visto de lado, recheio à mostra)
const PAO =
  g('#e0a35c', [[2, 3, 8, 2], [2, 7, 8, 2]])
  + g('#f7e7c8', [[3, 3, 2, 1], [7, 3, 2, 1]])
  + g('#e8788c', [[1, 5, 10, 2]])
  + g('#b8814a', [[2, 9, 8, 1]]);

// Rajada do Estoque de Vento (linhas de vento com folhinhas)
const VENTO =
  g('#cfe8f4', [[1, 3, 7, 1], [3, 6, 8, 1], [1, 9, 6, 1]])
  + g('#e8f4fa', [[8, 2, 2, 1], [10, 3, 1, 1], [11, 5, 1, 1], [7, 8, 2, 1], [6, 10, 1, 1]])
  + g('#9fd4a8', [[9, 6, 1, 1], [4, 2, 1, 1]]);

// Raízes da Amazônia: broto em cima, raízes abrindo embaixo
const RAIZ =
  g('#1e9e4e', [[5, 1, 2, 2], [3, 2, 2, 2], [7, 2, 2, 2]])
  + g('#5a8f3c', [[5, 4, 2, 4]])
  + g('#6d4520', [[4, 8, 4, 1], [2, 9, 3, 1], [7, 9, 3, 1], [1, 10, 2, 1], [9, 10, 2, 1]]);

// Mãozinha fazendo o L
const MAO_L =
  g('#e0ac69', [[4, 1, 2, 6], [6, 6, 4, 2], [4, 7, 2, 4]])
  + g('#c68642', [[4, 10, 2, 1], [9, 6, 1, 2]]);

// Seringa em órbita do #FiqueEmCasa (dose girando com rastro)
const VACINA_ORBITA =
  g('#d0ecf8', [[3, 4, 6, 3]])
  + g('#2ecc71', [[4, 5, 3, 1]])
  + g('#7a8087', [[9, 5, 3, 1], [1, 5, 2, 1]])
  + g('#ffe14d', [[0, 0, 1, 1], [11, 1, 1, 1], [1, 10, 1, 1], [10, 10, 1, 1]]);

// Porcentagem do ICMS (o leão morde em área)
const IMPOSTO =
  g('#f7d117', [[2, 2, 3, 3], [7, 7, 3, 3]])
  + g('#0b3d22', [[3, 3, 1, 1], [8, 8, 1, 1]])
  + g('#c1272d', [[8, 2, 2, 2], [6, 4, 2, 2], [4, 6, 2, 2], [2, 8, 2, 2]]);

const WEAPON_ICONS: Record<string, string> = {
  live_varanda: SOLUCO,        // Soluço Patriótico (#121)
  lulinha: MAO_L,              // Faz o L (#121)
  cachaca_51: PAO,             // Pão com Mortadela (#121)
  panelaco: VENTO,             // Estoque de Vento (#121)
  pombas_da_paz: RAIZ,         // Raízes da Amazônia (#121)
  carteira_trabalho: CTPS,
  cruz_credo: CRUZ,
  vampiros_madrugada: MORCEGO,
  cadeirada: CADEIRA,
  fake_news: MEGAFONE,
  pix_emendas: PIX,            // Aliados Políticos (#121)
  cpi_giratoria: VACINA_ORBITA, // #FiqueEmCasa (#121)
  rachadinha: IMPOSTO,         // ICMS (#121)
  mensalao: MENSALAO,
  oito_jan: EXPLOSAO,
  stf: GELO,
};

// -----------------------------------------------------------------------------
// Gabinete de ícones de UPGRADES passivos (issue #31)
// -----------------------------------------------------------------------------
// Carranca do gabinete do ódio
const ODIO =
  g('#c1272d', [[2, 2, 8, 8]])
  + g('#8f1d1d', [[2, 2, 8, 1], [2, 2, 1, 8]])
  + g('#4a0d0d', [[3, 4, 2, 1], [7, 4, 2, 1], [4, 8, 4, 1], [3, 7, 1, 1], [8, 7, 1, 1]])
  + g('#2c2f33', [[4, 5, 1, 1], [7, 5, 1, 1]]);

// Robô do zap
const ROBO =
  g('#2ecc71', [[5, 0, 2, 1], [5, 1, 1, 1]])
  + g('#9aa5ad', [[2, 2, 8, 7]])
  + g('#565b63', [[2, 2, 8, 1]])
  + g('#0b3d22', [[3, 4, 2, 2], [7, 4, 2, 2], [4, 7, 4, 1]])
  + g('#7a8087', [[4, 9, 1, 2], [7, 9, 1, 2]]);

// Escudo do foro privilegiado
const ESCUDO =
  g('#16324a', [[2, 1, 8, 6], [3, 7, 6, 1], [4, 8, 4, 1], [5, 9, 2, 1]])
  + g('#2a5580', [[3, 2, 2, 4]])
  + g('#f7d117', [[5, 3, 2, 2]]);

// Pergaminho da PEC
const PEC =
  g('#b3925c', [[2, 1, 8, 1], [2, 10, 8, 1]])
  + g('#e8d8b0', [[3, 2, 6, 8]])
  + g('#6d4520', [[4, 4, 4, 1], [4, 6, 4, 1], [4, 8, 3, 1]]);

// Urninha do impeachment express (cédula entrando)
const URNINHA =
  g('#f4f1ea', [[5, 1, 3, 3]])
  + g('#2ecc71', [[6, 2, 1, 1]])
  + g('#b3ab9c', [[2, 4, 8, 6]])
  + g('#8f8877', [[2, 4, 8, 1]])
  + g('#2c2f33', [[4, 5, 4, 1]]);

// Coração do auxílio
const CORACAO =
  g('#e74c3c', [[2, 2, 3, 2], [7, 2, 3, 2], [1, 3, 10, 3], [3, 6, 6, 2], [4, 8, 4, 1], [5, 9, 2, 1]])
  + g('#f5a8a0', [[3, 3, 1, 1]]);

// Seringa da Vacina
const SERINGA =
  g('#7a8087', [[0, 4, 1, 3], [1, 5, 1, 1]])
  + g('#d0ecf8', [[2, 4, 7, 3]])
  + g('#2ecc71', [[3, 5, 4, 1]])
  + g('#c0c6cc', [[9, 5, 3, 1]]);

// Aviãozinho do passaporte
const AVIAO =
  g('#e8eaee', [[1, 5, 9, 2], [10, 5, 1, 2], [1, 3, 2, 2]])
  + g('#c0c6cc', [[4, 2, 2, 3], [4, 7, 2, 3]])
  + g('#5dade2', [[3, 5, 1, 1], [5, 5, 1, 1], [7, 5, 1, 1]]);

// Ímã de currais eleitorais
const IMA =
  g('#c1272d', [[2, 1, 3, 7], [7, 1, 3, 7], [3, 8, 6, 2]])
  + g('#e8eaee', [[2, 1, 3, 2], [7, 1, 3, 2]])
  + g('#f7d117', [[1, 10, 1, 1], [5, 11, 2, 1], [10, 10, 1, 1]]);

const UPGRADE_ICONS: Record<string, string> = {
  gabinete_odio: ODIO,
  robos_whatsapp: ROBO,
  foro_privilegiado: ESCUDO,
  pec_vinganca: PEC,
  impeachment_express: URNINHA,
  auxilio_brasil: CORACAO,
  ze_gotinha: SERINGA,
  passaporte: AVIAO,
  ima_votos: IMA,
};

// Interrogação de "isso não devia aparecer" (fallback de id desconhecido)
const DESCONHECIDO =
  g('#f7d117', [[3, 1, 6, 2], [8, 3, 2, 2], [6, 5, 3, 1], [5, 6, 2, 2], [5, 9, 2, 2]]);

// -----------------------------------------------------------------------------
// Prêmios do Cassino da Maleta (issue #121): pixel art na esteira, zero emoji
// -----------------------------------------------------------------------------
// Estrela da emenda do relator / golpe de mestre
const ESTRELA_PREMIO =
  g('#f7d117', [[5, 1, 2, 2], [4, 3, 4, 1], [1, 4, 10, 2], [3, 6, 6, 1], [2, 7, 3, 2], [7, 7, 3, 2]])
  + g('#fff8c8', [[5, 4, 2, 1]]);

// Sirene da CPI da BET
const SIRENE =
  g('#c1272d', [[4, 2, 4, 4]])
  + g('#ff6b5e', [[5, 3, 1, 2]])
  + g('#565b63', [[3, 6, 6, 2]])
  + g('#ffe14d', [[1, 1, 1, 1], [10, 1, 1, 1], [0, 4, 1, 1], [11, 4, 1, 1]]);

// Fatia de pizza do "acabou em pizza"
const PIZZA =
  g('#d9a441', [[2, 2, 8, 2]])
  + g('#f7d117', [[3, 4, 6, 3], [4, 7, 4, 2], [5, 9, 2, 1]])
  + g('#c1272d', [[4, 5, 1, 1], [7, 5, 1, 1], [5, 7, 2, 1]]);

const CASINO_ICONS: Record<string, string> = {
  vida: CORACAO,          // plano de saúde: cura
  voto: URNINHA,          // compra de votos: XP pra chapa
  skill: ESTRELA_PREMIO,  // emenda do relator: upgrade grátis
  foro: ESCUDO,           // foro privilegiado: imunidade
  cpi: SIRENE,            // CPI da BET: confisco de votos
  nada: PIZZA,            // acabou em pizza
  golpe: ESTRELA_PREMIO,  // golpe de mestre: evolução (o card troca pelo ícone da arma)
};

export function casinoPrizeIcon(id: string, size = 28): string {
  return svg(CASINO_ICONS[id] ?? DESCONHECIDO, size);
}

// Armas supremas (#98) reaproveitam o santinho da arma base (nada de emoji
// novo — CLAUDE.md): mapa reverso evolução -> base montado uma vez
const EVOLVED_BASE: Record<string, string> = {};
for (const def of Object.values(WEAPON_TABLE)) {
  if (def.evolution) EVOLVED_BASE[def.evolution.into] = def.id;
}

// Ícone de arma/upgrade por (kind, id) — a urna, o HUD e o placar usam este
export function abilityIcon(kind: 'weapon' | 'upgrade', id: string, size = 20): string {
  const table = kind === 'weapon' ? WEAPON_ICONS : UPGRADE_ICONS;
  const art = table[id] ?? (kind === 'weapon' ? table[EVOLVED_BASE[id] ?? ''] : undefined);
  return svg(art ?? DESCONHECIDO, size);
}

// -----------------------------------------------------------------------------
// Selos da mesa diretora da urna (issue #100) e da Emenda de Relator (#93)
// -----------------------------------------------------------------------------
// Recontagem de Votos: setas em ciclo
const RECONTAGEM =
  g('#16324a', [
    [3, 1, 6, 1], [9, 2, 1, 3], [8, 4, 3, 1], [9, 5, 1, 1],
    [3, 10, 6, 1], [2, 7, 1, 3], [1, 7, 3, 1], [2, 6, 1, 1],
  ]);

// Cassação de Mandato: o X do carimbo (chunky, 2x2)
const CASSACAO =
  g('#c1272d', [
    [2, 2, 2, 2], [4, 4, 2, 2], [6, 6, 2, 2], [8, 8, 2, 2],
    [8, 2, 2, 2], [6, 4, 2, 2], [2, 8, 2, 2], [4, 6, 2, 2],
  ]);

// Abstenção: bandeira branca hasteada
const ABSTENCAO =
  g('#7a8087', [[3, 1, 1, 10]])
  + g('#f4f1ea', [[4, 2, 6, 3], [4, 5, 5, 1]])
  + g('#d8d3c8', [[4, 4, 6, 1]]);

const ACOES_URNA: Record<string, string> = {
  reroll: RECONTAGEM,
  banish: CASSACAO,
  skip: ABSTENCAO,
};

export function urnaActionIcon(action: 'reroll' | 'banish' | 'skip', size = 16): string {
  return svg(ACOES_URNA[action] ?? DESCONHECIDO, size);
}

// Selo dourado da Emenda de Relator: estrela com contorno
const SELO_DOURADO =
  g('#a8862a', [
    [5, 0, 2, 2], [4, 2, 4, 1], [0, 3, 12, 1], [1, 4, 10, 1], [2, 5, 8, 1],
    [2, 6, 3, 1], [7, 6, 3, 1], [1, 7, 3, 1], [8, 7, 3, 1],
  ])
  + g('#f7d117', [
    [5, 1, 2, 1], [5, 2, 2, 1], [2, 4, 8, 1], [3, 5, 6, 1],
    [3, 6, 2, 1], [7, 6, 2, 1],
  ]);

export function goldenSealIcon(size = 14): string {
  return svg(SELO_DOURADO, size);
}

// -----------------------------------------------------------------------------
// Ícones do placar da CPI e da party do HUD (issue #31: nada de emoji)
// -----------------------------------------------------------------------------
// Espadinha de contagem de abates (o "cassados por arma")
const ESPADA =
  g('#e8eaee', [[5, 0, 2, 7]])
  + g('#c0c6cc', [[6, 1, 1, 6]])
  + g('#f7d117', [[3, 7, 6, 1], [5, 11, 2, 1]])
  + g('#8a5a2b', [[5, 8, 2, 3]]);

// Caveira do aliado caído (aguardando o revive da coligação)
const CAVEIRA =
  g('#f4f1ea', [[3, 1, 6, 1], [2, 2, 8, 4], [3, 6, 6, 1], [4, 7, 4, 2]])
  + g('#2c2f33', [[3, 3, 2, 2], [7, 3, 2, 2], [5, 5, 2, 1]])
  + g('#d8d3c8', [[5, 7, 1, 2], [7, 7, 1, 2]]);

// Cara de zumbi político (caiu, ninguém reanimou, virou base hostil)
const ZUMBI =
  g('#1e9e4e', [[2, 1, 8, 2], [1, 2, 1, 2], [10, 2, 1, 2]])
  + g('#2ecc71', [[2, 3, 8, 6], [3, 9, 6, 1]])
  + g('#0b3d22', [[3, 4, 2, 2], [7, 5, 2, 1], [4, 7, 4, 1], [6, 8, 1, 1]]);

// Caixão do mandato definitivamente encerrado
const CAIXAO =
  g('#8a5a2b', [[4, 0, 4, 1], [3, 1, 6, 3], [4, 4, 4, 5], [5, 9, 2, 2]])
  + g('#6d4520', [[3, 1, 1, 3], [4, 4, 1, 5], [5, 9, 1, 2]])
  + g('#d4af37', [[5, 2, 2, 5], [4, 3, 4, 1]]);

export function killsIcon(size = 12): string {
  return svg(ESPADA, size);
}

const ESTADOS_JOGADOR: Record<string, string> = {
  downed: CAVEIRA,
  zombie: ZUMBI,
  dead: CAIXAO,
};

export function playerStateIcon(state: 'downed' | 'zombie' | 'dead', size = 12): string {
  return svg(ESTADOS_JOGADOR[state] ?? DESCONHECIDO, size);
}

// Barrinhas de sinal do ping (currentColor: herda o verde/amarelo/vermelho)
const SINAL =
  g('currentColor', [[1, 8, 2, 3], [4, 6, 2, 5], [7, 4, 2, 7], [10, 1, 2, 10]]);

export function pingIcon(size = 12): string {
  return svg(SINAL, size);
}

// -----------------------------------------------------------------------------
// Ícones de stats do cartão de candidato (comitê): HP e velocidade
// -----------------------------------------------------------------------------
const MINI_CORACAO =
  g('#e74c3c', [[2, 2, 3, 2], [7, 2, 3, 2], [1, 3, 10, 3], [3, 6, 6, 2], [4, 8, 4, 1], [5, 9, 2, 1]]);

const BOTA =
  g('#8a5a2b', [[3, 1, 3, 6], [3, 7, 6, 2]])
  + g('#3d2a12', [[3, 9, 7, 1]])
  + g('#f7d117', [[10, 3, 1, 1], [11, 5, 1, 1], [10, 7, 1, 1]]);

export function statIcon(stat: 'hp' | 'speed', size = 13): string {
  return svg(stat === 'hp' ? MINI_CORACAO : BOTA, size);
}

// -----------------------------------------------------------------------------
// Eleições com seed (#101/#97): ícones dos modos e dos modificadores do dia
// -----------------------------------------------------------------------------
// Sol nascendo sobre a urna: a eleição DO DIA
const SOL_URNA =
  g('#f7d117', [[5, 1, 2, 2], [2, 2, 1, 1], [9, 2, 1, 1], [4, 3, 4, 1], [3, 4, 6, 1]])
  + g('#f4f1ea', [[1, 6, 10, 5]])
  + g('#16324a', [[2, 8, 3, 2]])
  + g('#0a3318', [[3, 6, 6, 1], [7, 8, 1, 1], [9, 8, 1, 1]]);

// Folhinha de calendário com a semana riscada: a eleição DA SEMANA
const CALENDARIO =
  g('#e8eaee', [[1, 2, 10, 9]])
  + g('#d42a2a', [[1, 2, 10, 2]])
  + g('#565b63', [[3, 1, 1, 2], [8, 1, 1, 2]])
  + g('#565b63', [[2, 5, 2, 1], [5, 5, 2, 1], [8, 5, 2, 1], [2, 7, 2, 1], [5, 7, 2, 1], [8, 7, 2, 1]])
  + g('#f7d117', [[2, 9, 8, 1]]);

// TV de tubo berrando propaganda: Horário Eleitoral Gratuito
const TV_HORARIO =
  g('#565b63', [[4, 0, 1, 2], [7, 0, 1, 1], [6, 1, 1, 1]])
  + g('#8a8f98', [[1, 2, 10, 8]])
  + g('#3fa7ff', [[2, 3, 6, 6]])
  + g('#e8eaee', [[3, 4, 2, 2], [4, 6, 2, 1]])
  + g('#2c2c36', [[9, 3, 1, 1], [9, 5, 1, 1], [9, 7, 1, 1]]);

// Lâmpada apagada com X: Toffoli Apagou (sigilo de 100 anos no minimapa)
const LAMPADA_APAGADA =
  g('#565b63', [[4, 1, 4, 1], [3, 2, 1, 3], [8, 2, 1, 3], [4, 5, 4, 1], [5, 6, 2, 1]])
  + g('#2c2c36', [[4, 2, 4, 3]])
  + g('#8a8f98', [[5, 8, 2, 1], [5, 9, 2, 1]])
  + g('#d42a2a', [[9, 7, 1, 1], [11, 7, 1, 1], [10, 8, 1, 1], [9, 9, 1, 1], [11, 9, 1, 1]]);

// Dois "2" de urna eletrônica: Segundo Turno (dois bosses por horário)
const SEGUNDO_TURNO =
  g('#16324a', [[0, 2, 6, 8]])
  + g('#f7d117', [[1, 3, 3, 1], [3, 4, 1, 1], [1, 5, 3, 1], [1, 6, 1, 1], [1, 7, 3, 1]])
  + g('#4a1e1e', [[6, 2, 6, 8]])
  + g('#ff5252', [[7, 3, 3, 1], [9, 4, 1, 1], [7, 5, 3, 1], [7, 6, 1, 1], [7, 7, 3, 1]]);

// Cédula saindo da impressora: Voto Impresso (personagem fixo do dia)
const VOTO_IMPRESSO =
  g('#565b63', [[1, 2, 10, 4]])
  + g('#2c2c36', [[2, 3, 8, 1]])
  + g('#f4f1ea', [[3, 6, 6, 5]])
  + g('#565b63', [[4, 7, 4, 1], [4, 9, 4, 1]]);

// Estrela de fidelidade partidária (streak de dias consecutivos)
const FIDELIDADE = ESTRELA;

const ICONES_ELEICAO: Record<string, string> = {
  diaria: SOL_URNA,
  semanal: CALENDARIO,
  horario_eleitoral: TV_HORARIO,
  dinheiro_na_cueca: MALETA,
  toffoli_apagou: LAMPADA_APAGADA,
  segundo_turno: SEGUNDO_TURNO,
  voto_impresso: VOTO_IMPRESSO,
  fidelidade: FIDELIDADE,
};

/** Ícone pixel art dos modos/modificadores das eleições com seed (#101/#97). */
export function eleicaoPixelIcon(id: string, size = 20): string {
  return svg(ICONES_ELEICAO[id] ?? ESTRELA, size);
}

// -----------------------------------------------------------------------------
// Meta-progressão (#90/#94): cadeado do sigilo, maço de Caixa 2 e os ícones
// do Gabinete de Campanha — tudo pixel art, emoji só com mandato cassado
// -----------------------------------------------------------------------------

// Cadeado do "candidato sob sigilo de 100 anos"
const CADEADO =
  g('#b8940b', [[4, 1, 4, 1], [3, 2, 1, 3], [8, 2, 1, 3]])
  + g('#f7d117', [[2, 5, 8, 6]])
  + g('#8a6b12', [[2, 10, 8, 1]])
  + g('#14141a', [[5, 6, 2, 2], [5, 8, 1, 2]]);

export function cadeadoIcon(size = 20): string {
  return svg(CADEADO, size);
}

// Maço de Caixa 2: notas verdes com cinta dourada (HUD e Gabinete)
const CAIXA2 =
  g('#14141a', [[1, 3, 10, 1], [1, 9, 10, 1], [0, 4, 1, 5], [11, 4, 1, 5]])
  + g('#2ecc71', [[1, 4, 10, 5]])
  + g('#1a9e4a', [[2, 5, 2, 3], [8, 5, 2, 3]])
  + g('#f7d117', [[5, 4, 2, 5]]);

export function caixa2Icon(size = 18): string {
  return svg(CAIXA2, size);
}

// Ícones da loja: um por upgrade permanente do Gabinete de Campanha
const GAB_VERBA = // saco de dinheiro da emenda
  g('#8a5a2b', [[4, 1, 4, 1], [5, 2, 2, 1]])
  + g('#c98a3d', [[3, 3, 6, 1], [2, 4, 8, 6]])
  + g('#a06a2b', [[2, 9, 8, 1]])
  + g('#f7d117', [[5, 5, 2, 3], [4, 6, 4, 1]]);
const GAB_SAUDE = // cruz do plano de saúde (particular, claro)
  g('#f4f1ea', [[1, 1, 10, 10]])
  + g('#d5d9e0', [[1, 10, 10, 1]])
  + g('#d42a2a', [[5, 2, 2, 8], [2, 5, 8, 2]]);
const GAB_CARRO = // carro oficial preto com giroflex
  g('#d42a2a', [[5, 1, 2, 2]])
  + g('#14141a', [[3, 3, 6, 2], [1, 5, 10, 3]])
  + g('#4a5a74', [[4, 4, 4, 1]])
  + g('#2c2c38', [[2, 8, 2, 2], [8, 8, 2, 2]])
  + g('#e8eaee', [[3, 6, 2, 1], [7, 6, 2, 1]]);
const GAB_ASSESSOR = // megafone da assessoria
  g('#d8d3c8', [[1, 4, 2, 4]])
  + g('#565b63', [[3, 3, 3, 6], [6, 2, 1, 8]])
  + g('#f7d117', [[7, 1, 1, 10], [8, 3, 1, 6], [9, 5, 2, 2]]);
const GAB_BASE = // ímã de currais eleitorais
  g('#d42a2a', [[2, 1, 3, 5], [7, 1, 3, 5]])
  + g('#b02020', [[2, 5, 3, 3], [7, 5, 3, 3]])
  + g('#e8eaee', [[2, 8, 3, 2], [7, 8, 3, 2]])
  + g('#f7d117', [[5, 3, 2, 1], [4, 6, 4, 1]]);
const GAB_FUNDO = // cofrinho do fundo eleitoral
  g('#e88ab0', [[2, 3, 8, 5], [4, 2, 4, 1], [1, 5, 1, 2], [10, 4, 1, 2]])
  + g('#c86a90', [[2, 8, 2, 2], [8, 8, 2, 2]])
  + g('#14141a', [[5, 2, 2, 1], [8, 4, 1, 1]]);

const GAB_ICONES: Record<string, string> = {
  verba_gabinete: GAB_VERBA,
  plano_saude: GAB_SAUDE,
  carro_oficial: GAB_CARRO,
  assessor_imprensa: GAB_ASSESSOR,
  base_aliada: GAB_BASE,
  fundo_eleitoral: GAB_FUNDO,
};

export function gabineteIcon(upgradeId: string, size = 26): string {
  return svg(GAB_ICONES[upgradeId] ?? GAB_VERBA, size);
}

// -----------------------------------------------------------------------------
// Feitos (#99/#92/#95): selo do Diário Oficial, pasta CONFIDENCIAL dos
// dossiês e o arquivo de fichas da CPI — pixel art, como manda o regimento
// -----------------------------------------------------------------------------

// Selo de "PROMESSA CUMPRIDA": medalha dourada com fita verde-amarela
const SELO =
  g('#f7d117', [[3, 1, 6, 1], [2, 2, 8, 4], [3, 6, 6, 1]])
  + g('#b8940b', [[4, 3, 1, 2], [7, 3, 1, 2], [5, 5, 2, 1]])
  + g('#1e9e4e', [[3, 7, 2, 4], [4, 10, 1, 1]])
  + g('#f7d117', [[7, 7, 2, 4], [7, 10, 1, 1]]);

export function seloIcon(size = 20): string {
  return svg(SELO, size);
}

// Pasta de dossiê com tarja (a tarja CONFIDENCIAL é texto por cima, no CSS)
const DOSSIE =
  g('#8a5a2b', [[1, 2, 4, 1]])
  + g('#c98a3d', [[1, 3, 10, 7]])
  + g('#a06a2b', [[1, 9, 10, 1]])
  + g('#d42a2a', [[2, 5, 8, 2]])
  + g('#f4f1ea', [[3, 5, 1, 1], [5, 5, 1, 1], [7, 5, 1, 1]]);

export function dossieIcon(size = 20): string {
  return svg(DOSSIE, size);
}
